/**
 * Safe storage access — the bare API throws SecurityError on Safari ITP, Firefox
 * strict mode and in-app webviews (Instagram/Facebook/TikTok); even the
 * `if (localStorage)` guard throws, because the property accessor itself throws.
 * Persistence through these wrappers is best-effort: errors are logged, never thrown.
 */

const safeStorage = (which: 'localStorage' | 'sessionStorage') => ({
  getItem (key: string): string | null {
    try {
      return window[which].getItem(key)
    } catch (e) {
      console.error(e)
      return null
    }
  },
  setItem (key: string, value: string): void {
    try {
      window[which].setItem(key, value)
    } catch (e) {
      console.error(e)
    }
  },
  removeItem (key: string): void {
    try {
      window[which].removeItem(key)
    } catch (e) {
      console.error(e)
    }
  },
  clear (): void {
    try {
      window[which].clear()
    } catch (e) {
      console.error(e)
    }
  },
})

export const safeLocalStorage = safeStorage('localStorage')
export const safeSessionStorage = safeStorage('sessionStorage')

/**
 * Parse a value read out of storage. Returns null on a miss or on corrupted JSON
 * instead of throwing: a truncated write or a hand-edited entry otherwise crashes
 * the same code path on every re-entry until the visitor clears storage.
 */
export const parseStoredJson = <T>(raw: string | null): T | null => {
  if (!raw) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}
